import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Plus, X, Trash2, Zap } from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { toast } from 'sonner';
import ModeSelector, { EditorMode } from './ModeSelector';
import FlowSelector from './FlowSelector';
import VisualProcessingFlowEditor from './VisualProcessingFlowEditor';
import useProcessing from '../Processing/components/hooks/useProcessing';
import InfiniteCanvas from './InfiniteCanvas';
import UnifiedRightPanel from './UnifiedRightPanel';
import HierarchyPanel from './HierarchyPanel';
import CanvasToolbar from './CanvasToolbar';
import {
    selectScreen,
    selectElement,
    addScreenAtCenter,
    createSharingStationDevice,
    removeExperienceDevice,
    setActiveTab,
} from './utils/state/canvasSlice';
import { createScreen, createUIElement } from './utils/state/screensSlice';
import { addScreenToExperience } from './utils/state/experienceSlice';
import {
    updateLegacyFlow,
    addProcessingFlowFromDefinition,
} from './utils/state/processingFlowSlice';
import { ReplicateProvider, useReplicate } from './ReplicateContext';
import { ReplicateToolDialog } from './ReplicateToolDialog';
import NavigationLegend from './NavigationLegend';
import TemplateLibrary from './TemplateLibrary';
import HelpPane from './HelpPane';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import DebugPane from './components/DebugPane';
import useSchemaValidation from './hooks/useSchemaValidation';
// import { Tabs, TabsContent, TabsList, TabsTrigger } from '../shadComponents/ui/tabs';

interface VisualExperienceBuilderProps {
    screens: ScreenDefinition[]; // Main experience screens only
    allScreens: ScreenDefinition[]; // All screens (including sharing station screens)
    sharing: Sharing[];
    activationId: number;
    onExitEditor?: () => void;
    createSharingStation?: (name: string) => void;
    deleteSharingStation?: (name: string) => void;
    addScreenToSharingStation?: (
        sharingName: string,
        screen: ScreenDefinition,
    ) => void;
    deleteScreenFromSharingStation?: (
        sharingName: string,
        screenId: string,
    ) => void;
    updateSharingStation?: (oldName: string, newSharing: any) => void;
    onExport?: () => void;
    onImport?: () => void;
    onSave?: () => void;
    hasUnsavedChanges?: boolean;
    lastSaved?: Date;
    submitting?: boolean;
}

function VisualExperienceBuilderContent({
    screens,
    allScreens,
    sharing,
    activationId,
    onExitEditor,
    createSharingStation,
    deleteSharingStation,
    addScreenToSharingStation,
    deleteScreenFromSharingStation,
    updateSharingStation,
    onExport,
    onImport,
    onSave,
    hasUnsavedChanges = false,
    lastSaved,
    submitting = false,
}: VisualExperienceBuilderProps) {
    const dispatch = useDispatch();
    const { replicateMode, setReplicateMode } = useReplicate();

    // Schema validation
    const { validationResult, isValidating } =
        useSchemaValidation(activationId);

    // Get activeTab from Redux state
    const activeTab = useSelector((state: any) => state.canvas.activeTab);
    const [canvasSize, setCanvasSize] = useState({ width: 1200, height: 800 });
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [newSharingName, setNewSharingName] = useState('');
    const [showConnections, setShowConnections] = useState(true);
    const [showLegend, setShowLegend] = useState(false);
    const [showTemplates, setShowTemplates] = useState(false);
    const [showHelp, setShowHelp] = useState(false);

    // Mode selector state
    const [editorMode, setEditorMode] = useState<EditorMode>('design');
    const [selectedFlowId, setSelectedFlowId] = useState<string | null>(null);

    // Processing flow data
    const { processors, environments } = useProcessing();

    // Get flows from Redux state - use selector with shallow comparison
    const legacyFlows = useSelector(
        (state: any) => state.flows?.present?.legacyFlows || {},
    );

    // Memoize flows to prevent infinite loops
    const flows = useMemo(() => {
        console.log('Available legacy flows:', legacyFlows);
        return Object.keys(legacyFlows).map((flowId: string) => ({
            id: parseInt(flowId) || 0,
            enabled: true,
            environmentId: 1, // Default to local environment
            galleryId: activationId, // Use activation ID as gallery ID
            flowDefinition: JSON.stringify(legacyFlows[flowId] || []),
        }));
    }, [legacyFlows, activationId]);

    // Reset flow selection when switching back to design mode
    useEffect(() => {
        if (editorMode === 'design') {
            setSelectedFlowId(null);
        }
    }, [editorMode]);

    // Handle creating a new processing flow
    const handleCreateFlow = useCallback(() => {
        const newFlowId = `${activationId}-flow-${Date.now()}`;
        setSelectedFlowId(newFlowId);
        // The flow will be created when saved in the processing editor
    }, [activationId]);

    // Handle processing flow save
    const handleFlowSave = useCallback(
        (nodes: any[], enabled: boolean) => {
            if (selectedFlowId) {
                // Convert visual flow nodes back to legacy format with string arguments
                const flowDefinition = nodes.map((node) => {
                    // Ensure all arguments are strings
                    const stringArguments: { [key: string]: string } = {};
                    if (node.arguments && typeof node.arguments === 'object') {
                        Object.keys(node.arguments).forEach((key) => {
                            stringArguments[key] = String(node.arguments[key]);
                        });
                    }

                    return {
                        ...node.processor,
                        arguments: stringArguments,
                        saveOutput: node.saveOutput,
                    };
                });

                // Check if this is an existing flow or a new one
                const existingFlow = flows.find(
                    (f: any) => f.id.toString() === selectedFlowId,
                );

                if (existingFlow) {
                    // Update existing flow in Redux state
                    dispatch(
                        updateLegacyFlow({
                            id: selectedFlowId,
                            flowDefinition: flowDefinition,
                        }),
                    );
                    console.log(
                        'Flow updated:',
                        selectedFlowId,
                        flowDefinition,
                    );
                    toast.success('Processing flow updated successfully');
                } else {
                    // Create new flow in Redux state
                    dispatch(
                        addProcessingFlowFromDefinition({
                            id: selectedFlowId,
                            flow: flowDefinition,
                        }),
                    );
                    console.log(
                        'Flow created:',
                        selectedFlowId,
                        flowDefinition,
                    );
                    toast.success('Processing flow created successfully');
                }
            }
        },
        [selectedFlowId, flows, dispatch],
    );

    // Get the selected flow data - memoize to prevent infinite loops
    const selectedFlow = useMemo(() => {
        if (!selectedFlowId) return undefined;
        return flows?.find((f: any) => f.id.toString() === selectedFlowId);
    }, [selectedFlowId, flows]);

    // Delete sharing station dialog state
    const [deleteSharingDialog, setDeleteSharingDialog] = useState<{
        isOpen: boolean;
        sharingName: string;
    }>({
        isOpen: false,
        sharingName: '',
    });

    const handleCreateSharingStation = () => {
        if (newSharingName.trim() && createSharingStation) {
            const sharingStationName = newSharingName.trim();

            // Create the sharing station
            createSharingStation(sharingStationName);

            // Create device settings for the sharing station (inherits from main experience)
            dispatch(createSharingStationDevice(sharingStationName));

            setNewSharingName('');
            setShowCreateDialog(false);
            dispatch(setActiveTab(sharingStationName));
        }
    };

    const handleDeleteSharingStation = (name: string) => {
        setDeleteSharingDialog({
            isOpen: true,
            sharingName: name,
        });
    };

    const confirmDeleteSharingStation = () => {
        const { sharingName } = deleteSharingDialog;
        if (deleteSharingStation && sharingName) {
            deleteSharingStation(sharingName);

            // Remove device settings for the sharing station
            dispatch(removeExperienceDevice(sharingName));

            if (activeTab === sharingName) {
                dispatch(setActiveTab('main'));
            }
        }
        setDeleteSharingDialog({ isOpen: false, sharingName: '' });
    };

    const handleCreateScreen = (screenData: Partial<ScreenDefinition>) => {
        const screenId = screenData.id || `screen-${Date.now()}`;
        const newScreen: ScreenDefinition = {
            id: screenId,
            displayProperties: screenData.displayProperties || {
                backgroundColor: '#FFFFFF',
                backgroundMediaUrl: '',
                foregroundColor: '#000000',
                buttonColor: '#3B82F6',
                buttonTextColor: '#FFFFFF',
                horizontalPadding: 0,
                verticalPadding: 0,
            },
            stage: screenData.stage || {
                type: 'attract' as any,
                metadata: {},
            },
            elements: screenData.elements || [],
            navigateDestination: screenData.navigateDestination,
            navigateAfterTimeout: screenData.navigateAfterTimeout,
            resetAfterTimeout: screenData.resetAfterTimeout ?? null,
            metadata: screenData.metadata || {},
        };

        if (activeTab === 'main') {
            // Add to main experience
            dispatch(createScreen(newScreen));
            // Add screen to the experience's screen list
            dispatch(
                addScreenToExperience({
                    experienceId: activationId,
                    screenId: screenId,
                }),
            );
            // Position the new screen at the center of the canvas
            dispatch(
                addScreenAtCenter({
                    screenId,
                    canvasWidth: canvasSize.width,
                    canvasHeight: canvasSize.height,
                }),
            );
        } else {
            // Add to sharing station
            if (addScreenToSharingStation) {
                addScreenToSharingStation(activeTab, newScreen);
            }
        }
    };

    const handleDeleteScreen = (screenId: string) => {
        if (activeTab !== 'main' && deleteScreenFromSharingStation) {
            deleteScreenFromSharingStation(activeTab, screenId);
        }
    };

    const handleAddElementToSharingStation = useCallback(
        (
            sharingName: string,
            screenId: string,
            elementType: UIElementType,
            gridPosition: { h: string; v: string },
            groupId?: string,
        ) => {
            console.log('Adding element to sharing station:', {
                sharingName,
                screenId,
                elementType,
                gridPosition,
                groupId,
            });

            // Find the sharing station
            const sharingStation = sharing.find((s) => s.name === sharingName);
            if (!sharingStation) {
                console.error('Sharing station not found:', sharingName);
                return;
            }

            // Check if the screen ID is in the sharing station
            if (!sharingStation.screens.includes(screenId)) {
                console.error('Screen not found in sharing station:', screenId);
                return;
            }

            // Create the new element directly in the screens Redux store
            // This is the same approach used for main experience screens
            dispatch(
                createUIElement({
                    screenId,
                    elementType,
                    horizontalLocation: gridPosition.h,
                    verticalLocation: gridPosition.v,
                    layer: 0,
                    groupLocation: groupId,
                }),
            );
        },
        [sharing, dispatch],
    );

    // Dynamic canvas sizing based on available viewport
    useEffect(() => {
        const updateCanvasSize = () => {
            const container = document.getElementById('canvas-container');
            if (container) {
                const rect = container.getBoundingClientRect();
                setCanvasSize({
                    width: rect.width,
                    height: rect.height,
                });
            }
        };

        // Initial size calculation
        updateCanvasSize();

        // Update on window resize
        window.addEventListener('resize', updateCanvasSize);

        // Update when container size changes (e.g., fullscreen toggle)
        const resizeObserver = new ResizeObserver(updateCanvasSize);
        const container = document.getElementById('canvas-container');
        if (container) {
            resizeObserver.observe(container);
        }

        return () => {
            window.removeEventListener('resize', updateCanvasSize);
            resizeObserver.disconnect();
        };
    }, []);

    const { selectedScreenId, selectedElementId, isFullscreen } = useSelector(
        (state: any) => state.canvas,
    );

    // Note: currentScreen and selectedElement removed as they were unused

    // Update canvas size based on container
    useEffect(() => {
        const updateCanvasSize = () => {
            const container = document.getElementById('canvas-container');
            if (container) {
                const rect = container.getBoundingClientRect();
                setCanvasSize({
                    width: rect.width,
                    height: rect.height,
                });
            }
        };

        updateCanvasSize();
        window.addEventListener('resize', updateCanvasSize);

        // Update size when properties panel state might change
        const timer = setTimeout(updateCanvasSize, 100);

        return () => {
            window.removeEventListener('resize', updateCanvasSize);
            clearTimeout(timer);
        };
    }, [selectedScreenId, selectedElementId]);

    // Clear selection when switching tabs but preserve zoom/pan
    useEffect(() => {
        dispatch(selectScreen(undefined));
        dispatch(selectElement(undefined));
    }, [activeTab, dispatch]);

    // Recalculate canvas size when fullscreen changes
    useEffect(() => {
        const timer = setTimeout(() => {
            const container = document.getElementById('canvas-container');
            if (container) {
                const rect = container.getBoundingClientRect();
                setCanvasSize({
                    width: rect.width,
                    height: rect.height,
                });
            }
        }, 100); // Small delay to allow DOM to update

        return () => clearTimeout(timer);
    }, [isFullscreen]);

    // Global scroll prevention when elements are selected
    useEffect(() => {
        const preventGlobalScroll = (e: Event) => {
            const target = e.target as HTMLElement;
            const canvasContainer = document.getElementById('canvas-container');

            // If the scroll is happening on or within the canvas container, prevent it
            if (
                canvasContainer &&
                (target === canvasContainer || canvasContainer.contains(target))
            ) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();

                // Force reset scroll position
                canvasContainer.scrollTop = 0;
                canvasContainer.scrollLeft = 0;

                return false;
            }
        };

        // Add global scroll prevention
        document.addEventListener('scroll', preventGlobalScroll, {
            capture: true,
            passive: false,
        });
        window.addEventListener('scroll', preventGlobalScroll, {
            capture: true,
            passive: false,
        });

        return () => {
            document.removeEventListener('scroll', preventGlobalScroll, {
                capture: true,
            } as any);
            window.removeEventListener('scroll', preventGlobalScroll, {
                capture: true,
            } as any);
        };
    }, []);

    // Removed unused functions for cleaner code

    // Get screens for current tab
    const getCurrentScreens = () => {
        if (activeTab === 'main') {
            return screens; // Main experience screens only
        } else {
            const sharingStation = sharing.find((s) => s.name === activeTab);
            if (!sharingStation?.screens) {
                return [];
            }

            // Sharing station screens are stored as IDs, need to resolve to full screen objects
            // Use allScreens to resolve sharing station screen IDs
            return sharingStation.screens
                .map((screenId: string) =>
                    allScreens.find((screen) => screen.id === screenId),
                )
                .filter(
                    (screen): screen is ScreenDefinition =>
                        screen !== undefined,
                ); // Remove any undefined entries
        }
    };

    const currentScreens = getCurrentScreens();

    return (
        <div className="h-full w-full flex flex-col overflow-hidden">
            {/* Header with Mode Selector */}
            <div className="border-b border-border p-4 bg-background">
                <div className="flex items-center justify-between">
                    {/* Left side: Sharing tabs (only in design mode) or title */}
                    {editorMode === 'design' ? (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => dispatch(setActiveTab('main'))}
                                className={`px-4 py-2 text-sm font-medium rounded-md ${
                                    activeTab === 'main'
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                            >
                                Main Experience
                            </button>
                            {sharing.map((sharingStation) => (
                                <div
                                    key={sharingStation.name}
                                    className="flex items-center gap-1"
                                >
                                    <button
                                        onClick={() =>
                                            dispatch(
                                                setActiveTab(
                                                    sharingStation.name,
                                                ),
                                            )
                                        }
                                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                                            activeTab === sharingStation.name
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                        {sharingStation.name}
                                    </button>
                                    <button
                                        onClick={() =>
                                            handleDeleteSharingStation(
                                                sharingStation.name,
                                            )
                                        }
                                        className="p-1 text-muted-foreground hover:text-red-600 rounded"
                                        title={`Delete ${sharingStation.name}`}
                                    >
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}

                            {/* Add new sharing station button */}
                            <button
                                onClick={() => setShowCreateDialog(true)}
                                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-blue-600 border border-dashed border-border hover:border-blue-300 rounded-md flex items-center gap-1"
                                title="Add Sharing Station"
                            >
                                <Plus className="w-4 h-4" />
                                Add Sharing Station
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-4">
                            <h2 className="text-lg font-semibold text-foreground">
                                Processing Flow Editor
                            </h2>
                            {/* Move processing flow selector to left side */}
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-foreground"></label>
                                <FlowSelector
                                    value={
                                        selectedFlowId
                                            ? parseInt(selectedFlowId)
                                            : undefined
                                    }
                                    onChange={(value) => {
                                        console.log(
                                            'Flow selector change:',
                                            value,
                                        );
                                        if (value) {
                                            setSelectedFlowId(value.toString());
                                        }
                                    }}
                                    placeholder="Select a flow to edit..."
                                    className="min-w-48"
                                />
                            </div>
                        </div>
                    )}

                    {/* Right side: Mode Selector */}
                    <div className="flex items-center gap-4">
                        <ModeSelector
                            currentMode={editorMode}
                            onModeChange={setEditorMode}
                        />
                    </div>
                </div>
            </div>

            {/* Fixed Toolbar - Only show in design mode */}
            {editorMode === 'design' && (
                <CanvasToolbar
                    selectedScreenId={selectedScreenId}
                    onExitEditor={onExitEditor}
                    onCreateScreen={handleCreateScreen}
                    canvasWidth={canvasSize.width}
                    canvasHeight={canvasSize.height}
                    onExport={onExport}
                    onImport={onImport}
                    onSave={onSave}
                    hasUnsavedChanges={hasUnsavedChanges}
                    lastSaved={lastSaved}
                    submitting={submitting}
                    replicateMode={replicateMode}
                    onToggleReplicate={() => setReplicateMode(!replicateMode)}
                    showConnections={showConnections}
                    onToggleConnections={() =>
                        setShowConnections(!showConnections)
                    }
                    onToggleLegend={() => setShowLegend(!showLegend)}
                    onToggleTemplates={() => setShowTemplates(!showTemplates)}
                    onToggleHelp={() => setShowHelp(!showHelp)}
                    activationId={activationId}
                />
            )}

            {/* Main content area */}
            <div className="flex-1 flex overflow-hidden">
                {editorMode === 'design' ? (
                    <>
                        {/* Design Mode: Hierarchy Panel */}
                        <HierarchyPanel
                            screens={currentScreens}
                            activeTab={activeTab}
                            onDeleteScreen={handleDeleteScreen}
                            onCreateScreen={handleCreateScreen}
                        />

                        {/* Design Mode: Canvas area */}
                        <div
                            id="canvas-container"
                            className="flex-1 relative overflow-hidden"
                            style={{
                                scrollBehavior: 'auto',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                contain: 'layout style paint',
                                isolation: 'isolate',
                            }}
                            onScroll={(e) => {
                                // Prevent any scrolling behavior
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.scrollTop = 0;
                                e.currentTarget.scrollLeft = 0;
                                return false;
                            }}
                            onScrollCapture={(e) => {
                                // Also prevent on capture phase
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.scrollTop = 0;
                                e.currentTarget.scrollLeft = 0;
                                return false;
                            }}
                        >
                            <InfiniteCanvas
                                screens={currentScreens}
                                width={canvasSize.width}
                                height={canvasSize.height}
                                showConnections={showConnections}
                                onExitEditor={onExitEditor}
                                onCreateScreen={handleCreateScreen}
                                isMainExperience={activeTab === 'main'}
                                onAddElementToSharingStation={
                                    handleAddElementToSharingStation
                                }
                            />
                        </div>

                        {/* Design Mode: Unified Right Panel */}
                        <UnifiedRightPanel screens={currentScreens} />
                    </>
                ) : (
                    /* Processing Mode: Visual Processing Flow Editor */
                    <div className="flex-1 flex flex-col">
                        {!selectedFlowId ? (
                            /* No flow selected - show selection UI */
                            <div className="flex-1 flex items-center justify-center bg-muted/50">
                                <div className="text-center max-w-md">
                                    <Zap className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-foreground mb-2">
                                        No Processing Flow Selected
                                    </h3>
                                    <p className="text-sm text-muted-foreground mb-6">
                                        Use the flow selector above to choose a
                                        processing flow to edit, or create a new
                                        one.
                                    </p>

                                    {flows.length > 0 ? (
                                        <div className="space-y-3">
                                            <p className="text-sm font-medium text-foreground">
                                                Available Flows:
                                            </p>
                                            <div className="space-y-2">
                                                {flows.map((flow) => (
                                                    <div
                                                        key={flow.id}
                                                        className="flex items-center justify-between p-3 bg-background rounded-md border"
                                                    >
                                                        <div className="text-left">
                                                            <div className="text-sm font-medium">
                                                                Flow {flow.id}
                                                            </div>
                                                            <div className="text-xs text-muted-foreground">
                                                                {flow.flowDefinition
                                                                    ? `${
                                                                          JSON.parse(
                                                                              flow.flowDefinition,
                                                                          )
                                                                              .length
                                                                      } steps`
                                                                    : 'No steps'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        <Button onClick={handleCreateFlow}>
                                            <Plus className="w-4 h-4" />
                                            Create Your First Flow
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            /* Flow selected - show VisualProcessingFlowEditor */
                            <div className="flex-1 relative overflow-hidden">
                                <VisualProcessingFlowEditor
                                    flow={selectedFlow}
                                    processors={processors}
                                    environments={environments}
                                    onSave={handleFlowSave}
                                    onClose={() => setSelectedFlowId(null)}
                                    hideBackButton={true}
                                    onLaunchAssetPicker={(callback) => {
                                        // TODO: Integrate with asset picker
                                        callback(
                                            'https://example.com/asset.jpg',
                                        );
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Replicate Tool Dialog */}
            <ReplicateToolDialog />

            {/* Navigation Legend */}
            {showLegend && (
                <NavigationLegend
                    isOpen={showLegend}
                    onClose={() => setShowLegend(false)}
                />
            )}

            {/* Template Library */}
            {showTemplates && (
                <TemplateLibrary
                    isOpen={showTemplates}
                    onClose={() => setShowTemplates(false)}
                    onCreateScreen={handleCreateScreen}
                />
            )}

            {/* Help Pane */}
            <HelpPane isOpen={showHelp} onClose={() => setShowHelp(false)} />

            {/* Create Sharing Station Dialog */}
            {showCreateDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-background rounded-lg p-6 w-96 max-w-md mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-foreground">
                                Create Sharing Station
                            </h3>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                    setShowCreateDialog(false);
                                    setNewSharingName('');
                                }}
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Sharing Station Name
                            </label>
                            <Input
                                type="text"
                                value={newSharingName}
                                onChange={(e) =>
                                    setNewSharingName(e.target.value)
                                }
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleCreateSharingStation();
                                    }
                                }}
                                placeholder="Enter sharing station name..."
                                autoFocus
                            />
                        </div>

                        <div className="flex justify-end gap-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowCreateDialog(false);
                                    setNewSharingName('');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleCreateSharingStation}
                                disabled={!newSharingName.trim()}
                            >
                                Create
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Sharing Station Confirmation Dialog */}
            <DeleteConfirmDialog
                isOpen={deleteSharingDialog.isOpen}
                onClose={() =>
                    setDeleteSharingDialog({ isOpen: false, sharingName: '' })
                }
                onConfirm={confirmDeleteSharingStation}
                title="Delete Sharing Station"
                message={`Are you sure you want to delete the sharing station "${deleteSharingDialog.sharingName}"? This action cannot be undone.`}
                confirmButtonText="Delete Sharing Station"
            />

            {/* Debug Pane */}
            <DebugPane
                validationResult={validationResult}
                isLoading={isValidating}
            />
        </div>
    );
}

export default function VisualExperienceBuilder(
    props: VisualExperienceBuilderProps,
) {
    return (
        <ReplicateProvider>
            <VisualExperienceBuilderContent {...props} />
        </ReplicateProvider>
    );
}
